import type { Execution, Order as SDKOrder, Position as SDKPosition } from "@ekidenfi/ts-sdk";

import { BN } from "@utils/BN";

import type { ExecutionWithVault, Fill, FillList, Market, Order, OrderList, Position, PositionList } from "@src/api";

const findMarketAddr = (symbol: string, marketsMap?: Map<string, Market>): string => {
  if (!marketsMap || marketsMap.size === 0) {
    return symbol;
  }
  for (const market of marketsMap.values()) {
    if (market.symbol === symbol) {
      return market.addr;
    }
  }
  return symbol;
};

export const convertSDKOrderToOrder = (order: SDKOrder, marketsMap?: Map<string, Market>): Order => {
  const createdTime =
    typeof order.created_time === "number" ? order.created_time : Number.parseInt(order.created_time, 10) || 0;
  const updatedTime =
    typeof order.updated_time === "number" ? order.updated_time : Number.parseInt(order.updated_time, 10) || 0;

  return {
    id: 0,
    created_at: createdTime,
    updated_at: updatedTime,
    market_addr: findMarketAddr(order.symbol, marketsMap),
    price: order.price,
    seq: "0",
    sid: order.order_id,
    side: order.side === "Buy" ? "buy" : "sell",
    size: order.qty,
    cum_exec_qty: order.cum_exec_qty || "0",
    status: order.order_status.toLowerCase(),
    type: order.order_type.toLowerCase(),
    sub_account_address: order.sub_account_address,
    leverage: Number(order.leverage) || 1,
    stop_loss: order.tpsl?.stop_loss
      ? "market" in order.tpsl.stop_loss
        ? order.tpsl.stop_loss.market.trigger_price
        : order.tpsl.stop_loss.limit.trigger_price
      : undefined,
    take_profit: order.tpsl?.take_profit
      ? "market" in order.tpsl.take_profit
        ? order.tpsl.take_profit.market.trigger_price
        : order.tpsl.take_profit.limit.trigger_price
      : undefined,
    trigger_price: order.trigger?.trigger_price,
  };
};

export const convertSDKOrderListToOrderList = (orders: SDKOrder[], marketsMap?: Map<string, Market>): OrderList => {
  return orders.map((order) => convertSDKOrderToOrder(order, marketsMap));
};

export const convertExecutionToFill = (
  execution: Execution,
  _userAddr?: string | null,
  marketsMap?: Map<string, Market>,
): Fill => {
  const execWithVault = execution as Execution & ExecutionWithVault;
  const size = execution.exec_qty || "0";
  const price = execution.exec_price || "0";
  const marketAddr = findMarketAddr(execution.symbol, marketsMap);
  const execTime =
    typeof execution.exec_time === "number" ? execution.exec_time : Number.parseInt(execution.exec_time, 10) || 0;
  const userAddr = execution.sub_account_address;

  const value = execWithVault.exec_value || new BN(size).multipliedBy(price).toString();
  const feeRate = Number(execution.fee_rate) || 0;
  const execValue = Number(execWithVault.exec_value) || Number(size) * Number(price);
  const fee = ((execValue * feeRate) / 1000000).toFixed(6);

  return {
    id: 0,
    created_at: execTime,
    updated_at: execTime,
    maker_leverage: 1,
    taker_leverage: 1,
    market_addr: marketAddr,
    price: price.toString(),
    sid: execution.exec_id,
    side: execution.side === "Buy" ? "buy" : "sell",
    size: size.toString(),
    value: value.toString(),
    fee: fee.toString(),
    taker_order_sid: execution.order_id,
    taker_addr: userAddr,
    maker_order_sid: execution.order_id,
    maker_addr: userAddr,
    seq: execution.seq?.toString() || "0",
  };
};

export const convertExecutionListToFillList = (
  executions: Execution[],
  userAddr?: string | null,
  marketsMap?: Map<string, Market>,
): FillList => {
  return executions.map((exec) => convertExecutionToFill(exec, userAddr, marketsMap));
};

export const convertSDKPositionToPosition = (position: SDKPosition, marketsMap?: Map<string, Market>): Position => {
  const marketAddr = findMarketAddr(position.symbol, marketsMap);
  const createdTime =
    typeof position.created_time === "number" ? position.created_time : Number.parseInt(position.created_time, 10) || 0;
  const updatedTime =
    typeof position.updated_time === "number" ? position.updated_time : Number.parseInt(position.updated_time, 10) || 0;

  return {
    id: 0,
    created_at: createdTime,
    updated_at: updatedTime,
    market_addr: marketAddr,
    sub_account_address: position.sub_account_address,
    sid: position.symbol,
    size: Number(position.size),
    price: position.avg_price,
    margin: position.position_balance,
    funding_index: "0",
    epoch: "0",
    side: position.side === "Buy" ? "buy" : "sell",
    entry_price: position.avg_price,
    is_cross: position.margin_mode === "Cross",
    mark_price: position.mark_price,
    unrealized_pnl: Number(position.unrealized_pnl),
    realized_pnl_delta: 0,
    realized_pnl_cum: Number(position.realized_pnl_cum),
    seq: position.seq?.toString(),
    liq_price: Number(position.liq_price),
    leverage: Number(position.leverage),
    tp_price: position.take_profit ?? undefined,
    sl_price: position.stop_loss ?? undefined,
    unrealized_funding: (position as any).unrealized_funding ?? "0",
  };
};

export const convertSDKPositionListToPositionList = (
  positions: SDKPosition[],
  marketsMap?: Map<string, Market>,
): PositionList => {
  return positions.map((pos) => convertSDKPositionToPosition(pos, marketsMap));
};

export const convertOrderResponseToOrder = convertSDKOrderToOrder;
export const convertOrderResponseListToOrderList = convertSDKOrderListToOrderList;
export const convertFillResponseToFill = convertExecutionToFill;
export const convertFillResponseListToFillList = convertExecutionListToFillList;
export const convertPositionResponseToPosition = convertSDKPositionToPosition;
export const convertPositionResponseListToPositionList = convertSDKPositionListToPositionList;
